'use client';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import React from 'react';
import {
   Box, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader, DrawerOverlay,
   Flex, Icon, Input, InputGroup, Spinner, Text, useColorMode, useColorModeValue,
   useDisclosure, useOutsideClick,
} from '@chakra-ui/react';
import Select from 'react-select';
import { GridItem } from '@chakra-ui/react';
import nodata from 'assets/img/nfts/no data.svg';
import { Button, Image } from '@chakra-ui/react';
import Download from '../../../../assets/img/nfts/download.svg';
import xls from '../../../../../src/assets/img/nfts/xls.svg';
import back from '../../../../../src/assets/img/nfts/back.svg';
import { Popover, PopoverTrigger, PopoverContent, PopoverBody } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { getReportTds } from '../../../../redux/actions/reports';
import { useGlobal , useUser} from '../../../../contexts';
import { ChevronDownIcon, ChevronUpIcon, SearchIcon } from '@chakra-ui/icons';
import { getShareCompanyOptions } from 'utils/util';
import DotIcon from '../../../../assets/img/nfts/dot.svg';
import { renderDynamicDropdown } from 'components/ui/DynamicDropdown';
import { LuRefreshCcw } from 'react-icons/lu';
import PeriodDatePicker from 'components/RangePicker/PeriodDatePicker';
import { motion } from 'framer-motion';
import { MdCompress, MdExpand } from 'react-icons/md';
import Loader from 'views/admin/default/Loader';
import XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const MotionFlex = motion(Flex);
const MotionSearchIcon = motion(SearchIcon);
const DEBOUNCE_DELAY = 500;

const fmt = (v) => {
   if (v === null || v === undefined || v === '') return '—';
   const n = Number(v);
   if (isNaN(n) || n === 0) return '—';
   return `₹${Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtAmt = (v) => {
   if (v === null || v === undefined || v === '') return '₹0.00';
   const n = Number(v);
   if (isNaN(n)) return '₹0.00';
   return `₹${Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtExport = (v) => {
   const n = Number(v);
   if (isNaN(n)) return '0.00';
   return Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const TOTAL_COLS = 8;
const LABEL_COLSPAN = 3;

const base = (extra = {}) => ({
   padding: '8px 12px',
   verticalAlign: 'middle',
   whiteSpace: 'nowrap',
   border: 'none',
   ...extra,
});

const LabelValue = ({ label, value, labelColor, valueColor, valueFontSize = '13px', valueFontWeight = 700 }) => (
   <Flex align="center" justify="flex-end" gap="6px" whiteSpace="nowrap">
      <Text as="span"
         fontSize="9px" fontWeight={600}
         color={labelColor}
         textTransform="uppercase"
         letterSpacing="0.07em">
         {label}
      </Text>
      <Text as="span"
         fontSize={valueFontSize}
         fontWeight={valueFontWeight}
         color={valueColor}>
         {value}
      </Text>
   </Flex>
);

const InvoiceSubHeader = React.memo(({ isDark }) => {
   const bg = isDark ? '#1a2840' : '#eef2f8';
   const clr = isDark ? '#90aec6' : '#4b5563';
   const bdr = isDark ? '#2d3748' : '#d8e0ed';
   const cols = [
      { label: 'Date', align: 'left', pl: '56px' },
      { label: 'Doc #', align: 'left' },
      { label: 'Daybook', align: 'left' },
      { label: 'TDS Rate', align: 'right' },
      { label: 'Net Amount', align: 'right' },
      { label: 'TDS Apply On', align: 'right' },
      { label: 'TDS Amount', align: 'right' },
      { label: 'Type', align: 'center' },
   ];
   return (
      <tr>
         {cols.map(({ label, align, pl }) => (
            <td key={label} style={{
               background: bg, color: clr, fontSize: '10px', fontWeight: 700,
               textTransform: 'uppercase', letterSpacing: '0.05em',
               padding: '6px 12px', textAlign: align,
               borderTop: `1px solid ${bdr}`, borderBottom: `1px solid ${bdr}`,
               whiteSpace: 'nowrap', ...(pl ? { paddingLeft: pl } : {}),
            }}>
               {label}
            </td>
         ))}
      </tr>
   );
});

const CompanyRow = React.memo(({ company, isExpanded, onToggle, isDark, totalAmount, totalTds }) => {
   const bg = isDark ? '#1e2d4a' : '#eaf0fb';
   const bdr = isDark ? '#3a4a6a' : '#c5d3ec';
   return (
      <tr onClick={onToggle} style={{ cursor: 'pointer' }}>
         <td colSpan={LABEL_COLSPAN} style={{
            ...base({ background: bg, borderBottom: `2px solid ${bdr}` }),
            borderLeft: `4px solid ${isDark ? '#4299e1' : '#2b6cb0'}`,
         }}>
            <Flex align="center" gap={2}>
               <Icon as={isExpanded ? ChevronUpIcon : ChevronDownIcon}
                  boxSize="13px" color="#718096" flexShrink={0} />
               <Text as="span" fontSize="15px" fontWeight={800} color={isDark ? '#f0f4f8' : '#101011ff'}>
                  {company.companyName}
               </Text>
               <Text as="span" fontSize="12px" color={isDark ? '#90aec6' : '#24262bff'} ml={1}>
                  GSTIN: {company.companyGstin} &nbsp;|&nbsp; PAN: {company.companyPan}
               </Text>
            </Flex>
         </td>
         <td style={{ ...base({ background: bg, borderBottom: `2px solid ${bdr}` }) }} />
         <td style={{ ...base({ background: bg, borderBottom: `2px solid ${bdr}` }), textAlign: 'right' }}>
            <LabelValue
               label="Net Amount"
               value={fmtAmt(totalAmount)}
               labelColor={isDark ? '#90aec6' : '#718096'}
               valueColor={isDark ? '#f0f4f8' : '#1a202c'}
               valueFontSize="13px" valueFontWeight={700}
            />
         </td>
         <td style={{ ...base({ background: bg, borderBottom: `2px solid ${bdr}` }) }} />
         <td style={{ ...base({ background: bg, borderBottom: `2px solid ${bdr}` }), textAlign: 'right' }}>
            <LabelValue
               label="TDS Amount"
               value={fmtAmt(totalTds)}
               labelColor={isDark ? '#fc8181' : '#c53030'}
               valueColor={isDark ? '#fc8181' : '#c53030'}
               valueFontSize="13px" valueFontWeight={700}
            />
         </td>
         <td style={{ ...base({ background: bg, borderBottom: `2px solid ${bdr}` }) }} />
      </tr>
   );
});

const HeadRow = React.memo(({ label, isDark, totalAmount, totalTds, accentColor }) => {
   const bg = isDark ? '#142035' : '#f0f4fb';
   const bdr = isDark ? '#2a3a55' : '#dce4f0';
   const accent = accentColor || (isDark ? '#63b3ed' : '#3182ce');
   return (
      <tr>
         <td colSpan={LABEL_COLSPAN} style={{
            ...base({ background: bg, borderBottom: `1px solid ${bdr}` }),
            borderLeft: `3px solid ${accent}`, paddingLeft: '24px',
         }}>
            <Text as="span" fontSize="14px" fontWeight={700} color={isDark ? '#bee3f8' : '#2b6cb0'}>
               {label}
            </Text>
         </td>
         <td style={{ ...base({ background: bg, borderBottom: `1px solid ${bdr}` }) }} />
         <td style={{ ...base({ background: bg, borderBottom: `1px solid ${bdr}` }), textAlign: 'right' }}>
            <LabelValue
               label="Net Amount"
               value={fmtAmt(totalAmount)}
               labelColor={isDark ? '#a0aec0' : '#718096'}
               valueColor={isDark ? '#e2e8f0' : '#1a202c'}
               valueFontSize="12px" valueFontWeight={700}
            />
         </td>
         <td style={{ ...base({ background: bg, borderBottom: `1px solid ${bdr}` }) }} />
         <td style={{ ...base({ background: bg, borderBottom: `1px solid ${bdr}` }), textAlign: 'right' }}>
            <LabelValue
               label="TDS Amount"
               value={fmtAmt(totalTds)}
               labelColor={isDark ? '#fc8181' : '#c53030'}
               valueColor={isDark ? '#fc8181' : '#c53030'}
               valueFontSize="12px" valueFontWeight={700}
            />
         </td>
         <td style={{ ...base({ background: bg, borderBottom: `1px solid ${bdr}` }) }} />
      </tr>
   );
});

const PartyRow = React.memo(({ party, isExpanded, onToggle, isDark, isSummary }) => {
   const bg = isDark ? '#0f1a2e' : '#ffffff';
   const bdr = isDark ? '#22304a' : '#edf0f7';
   return (
      <tr
         onClick={isSummary ? undefined : onToggle}
         style={{ cursor: isSummary ? 'default' : 'pointer' }}
      >
         <td colSpan={LABEL_COLSPAN} style={{
            ...base({ background: bg, borderBottom: `1px solid ${bdr}` }),
            borderLeft: `3px solid ${isDark ? '#68d391' : '#38a169'}`,
         }}>
            <Flex align="center" gap={2}>
               {!isSummary && (
                  <Icon as={isExpanded ? ChevronUpIcon : ChevronDownIcon}
                     boxSize="12px" color={isDark ? '#718096' : '#a0aec0'} flexShrink={0} />
               )}
               <Text as="span" fontSize="14px" fontWeight={600} color={isDark ? '#f0f4f8' : '#111827'}>
                  {party.partyName}
               </Text>
               {party.partyPan && (
                  <Text as="span" fontSize="12px" color={isDark ? '#718096' : '#1b1b1dff'}>
                     PAN: {party.partyPan}
                  </Text>
               )}
            </Flex>
         </td>
         <td style={{ ...base({ background: bg, borderBottom: `1px solid ${bdr}` }) }} />
         <td style={{ ...base({ background: bg, borderBottom: `1px solid ${bdr}` }), textAlign: 'right' }}>
            <LabelValue
               label="Net Amount"
               value={fmtAmt(party.totalAmount)}
               labelColor={isDark ? '#a0aec0' : '#718096'}
               valueColor={isDark ? '#e2e8f0' : '#1a202c'}
               valueFontSize="12px" valueFontWeight={600}
            />
         </td>
         <td style={{ ...base({ background: bg, borderBottom: `1px solid ${bdr}` }) }} />
         <td style={{ ...base({ background: bg, borderBottom: `1px solid ${bdr}` }), textAlign: 'right' }}>
            <LabelValue
               label="TDS Amount"
               value={fmtAmt(party.totalTdsAmount)}
               labelColor={isDark ? '#fc8181' : '#c53030'}
               valueColor={isDark ? '#fc8181' : '#c53030'}
               valueFontSize="12px" valueFontWeight={700}
            />
         </td>
         <td style={{ ...base({ background: bg, borderBottom: `1px solid ${bdr}` }) }} />
      </tr>
   );
});

const InvoiceRow = React.memo(({ invoice, idx, isDark }) => {
   const even = idx % 2 === 0;
   const bg = isDark ? (even ? 'transparent' : 'rgba(255,255,255,0.02)') : (even ? '#ffffff' : '#fafbfd');
   const bdr = isDark ? '#1e2d44' : '#eef1f7';
   const clr = isDark ? '#e2e8f0' : '#111827';
   const muted = isDark ? '#718096' : '#6b7280';
   return (
      <tr
         onMouseEnter={e => Array.from(e.currentTarget.cells).forEach(c => { c.style.background = isDark ? 'rgba(255,255,255,0.05)' : '#f0f7ff'; })}
         onMouseLeave={e => Array.from(e.currentTarget.cells).forEach(c => { c.style.background = bg; })}
         style={{ transition: 'background 0.1s' }}
      >
         <td style={{ ...base({ background: bg, borderBottom: `1px solid ${bdr}` }), paddingLeft: '56px', fontSize: '12px', color: clr }}>
            {invoice.transactionAt}
         </td>
         <td style={{ ...base({ background: bg, borderBottom: `1px solid ${bdr}` }), fontSize: '12px', color: clr }}>
            <span style={{ fontWeight: 600 }}>{invoice.docno}</span>
         </td>
         <td style={{ ...base({ background: bg, borderBottom: `1px solid ${bdr}` }), fontSize: '12px', color: clr }}>
            <span style={{ display: 'block', maxWidth: '175px', overflow: 'hidden', textOverflow: 'ellipsis' }} title={invoice.daybookName}>
               {invoice.daybookName}
            </span>
         </td>
         <td style={{ ...base({ background: bg, borderBottom: `1px solid ${bdr}` }), textAlign: 'right', fontSize: '12px' }}>
            <span style={{ fontWeight: 600, color: isDark ? '#f6ad55' : '#b7791f' }}>{invoice.tdsRate}%</span>
         </td>
         <td style={{ ...base({ background: bg, borderBottom: `1px solid ${bdr}` }), textAlign: 'right', fontSize: '12px', color: clr }}>
            {fmtAmt(invoice.netAmount)}
         </td>
         <td style={{ ...base({ background: bg, borderBottom: `1px solid ${bdr}` }), textAlign: 'right', fontSize: '12px', color: muted }}>
            {fmt(invoice.tdsApplyOnAmt)}
         </td>
         <td style={{ ...base({ background: bg, borderBottom: `1px solid ${bdr}` }), textAlign: 'right', fontSize: '12px' }}>
            <span style={{ fontWeight: 700, color: isDark ? '#fc8181' : '#c53030' }}>{fmtAmt(invoice.tdsAmount)}</span>
         </td>
         <td style={{ ...base({ background: bg, borderBottom: `1px solid ${bdr}` }), textAlign: 'center' }}>
            <span style={{
               display: 'inline-block', padding: '2px 9px', borderRadius: '999px',
               fontSize: '10px', fontWeight: 600, textTransform: 'capitalize',
               background: invoice.type === 'sales' ? (isDark ? 'rgba(72,187,120,0.15)' : '#f0fff4') : (isDark ? 'rgba(99,179,237,0.15)' : '#ebf8ff'),
               color: invoice.type === 'sales' ? (isDark ? '#68d391' : '#276749') : (isDark ? '#63b3ed' : '#2b6cb0'),
               border: `1px solid ${invoice.type === 'sales' ? (isDark ? 'rgba(72,187,120,0.3)' : '#c6f6d5') : (isDark ? 'rgba(99,179,237,0.3)' : '#bee3f8')}`,
            }}>
               {invoice.type}
            </span>
         </td>
      </tr>
   );
});

const DaybookRows = React.memo(({ data, isDark, expandedItems, toggleExpand, viewMode }) => {
   const rows = [];
   const isDetail = viewMode === 'detail';

   data.forEach((company) => {
      const cKey = `c-${company.companyId}`;
      const isCompExp = expandedItems[cKey] !== false;
      const cTotals = company.tdsHeads?.reduce((a, h) => {
         h.parties?.forEach((p) => { a.amount += p.totalAmount || 0; a.tds += p.totalTdsAmount || 0; });
         return a;
      }, { amount: 0, tds: 0 }) || { amount: 0, tds: 0 };

      rows.push(
         <CompanyRow key={cKey} company={company} isExpanded={isCompExp}
            onToggle={() => toggleExpand(cKey)} isDark={isDark}
            totalAmount={cTotals.amount} totalTds={cTotals.tds} />
      );
      if (!isCompExp) return;

      company.tdsHeads?.forEach((head) => {
         const hKey = `h-${company.companyId}-${head.tdsAccountId}`;
         const hTotals = head.parties?.reduce((a, p) => {
            a.amount += p.totalAmount || 0; a.tds += p.totalTdsAmount || 0; return a;
         }, { amount: 0, tds: 0 }) || { amount: 0, tds: 0 };

         rows.push(
            <HeadRow key={hKey} label={head.tdsHeadName} isDark={isDark}
               totalAmount={hTotals.amount} totalTds={hTotals.tds}
               accentColor={isDark ? '#63b3ed' : '#3182ce'} />
         );

         head.parties?.forEach((party) => {
            const pKey = `p-${company.companyId}-${head.tdsAccountId}-${party.partyAccountId}`;
            const isPartyExp = isDetail ? expandedItems[pKey] !== false : expandedItems[pKey] === true;

            rows.push(
               <PartyRow key={pKey} party={party} isExpanded={isPartyExp}
                  onToggle={() => toggleExpand(pKey)} isDark={isDark}
                  isSummary={!isDetail} />
            );

            if (!isDetail || !isPartyExp) return;
            if (party.invoices?.length > 0) rows.push(<InvoiceSubHeader key={`sh-${pKey}`} isDark={isDark} />);
            party.invoices?.forEach((inv, idx) =>
               rows.push(<InvoiceRow key={`inv-${inv.id || idx}-${idx}`} invoice={inv} idx={idx} isDark={isDark} />)
            );
         });
      });
   });

   return <>{rows}</>;
});

const NatureRows = React.memo(({ data, isDark, expandedItems, toggleExpand, viewMode }) => {
   const rows = [];
   const isDetail = viewMode === 'detail';

   data.forEach((company) => {
      const cKey = `nc-${company.companyId}`;
      const isCompExp = expandedItems[cKey] !== false;
      const cTotals = company.natures?.reduce((a, n) => {
         a.amount += n.totalAmount || 0; a.tds += n.totalTdsAmount || 0; return a;
      }, { amount: 0, tds: 0 }) || { amount: 0, tds: 0 };

      rows.push(
         <CompanyRow key={cKey} company={company} isExpanded={isCompExp}
            onToggle={() => toggleExpand(cKey)} isDark={isDark}
            totalAmount={cTotals.amount} totalTds={cTotals.tds} />
      );
      if (!isCompExp) return;

      company.natures?.forEach((nature, natIdx) => {
         const nKey = `nn-${company.companyId}-${natIdx}`;

         rows.push(
            <HeadRow key={nKey}
               label={`Nature of Payment: ${nature.natureOfPayment || 'NONE'}`}
               isDark={isDark} totalAmount={nature.totalAmount} totalTds={nature.totalTdsAmount}
               accentColor={isDark ? '#4fd1c5' : '#319795'} />
         );

         nature.parties?.forEach((party) => {
            const pKey = `np-${company.companyId}-${natIdx}-${party.partyAccountId}`;
            const isPartyExp = isDetail ? expandedItems[pKey] !== false : expandedItems[pKey] === true;

            rows.push(
               <PartyRow key={pKey} party={party} isExpanded={isPartyExp}
                  onToggle={() => toggleExpand(pKey)} isDark={isDark}
                  isSummary={!isDetail} />
            );

            if (!isDetail || !isPartyExp) return;
            if (party.invoices?.length > 0) rows.push(<InvoiceSubHeader key={`sh-${pKey}`} isDark={isDark} />);
            party.invoices?.forEach((inv, idx) =>
               rows.push(<InvoiceRow key={`ninv-${inv.id || idx}-${idx}`} invoice={inv} idx={idx} isDark={isDark} />)
            );
         });
      });
   });

   return <>{rows}</>;
});

const exportToExcel = (tdsData, isDaybookView, dateRange, reportType, viewMode) => {
   if (!tdsData || tdsData.length === 0) { alert('No data to export'); return; }
   const isDetail = viewMode === 'detail';

   try {
      const num = (v) => { const n = Number(v); return isNaN(n) ? 0 : Math.abs(n); };
      const fmtN = (v) => num(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const argb = (hex) => 'FF' + hex.replace('#', '').toUpperCase();

      const C = {
         titleBg: argb('#1a202c'), titleFg: argb('#ffffff'),
         metaBg: argb('#2d3748'), metaFg: argb('#a0aec0'),
         compBg: argb('#eaf0fb'), compFg: argb('#1a202c'),
         compAccent: argb('#2b6cb0'), compBorder: argb('#c5d3ec'),
         headBg: argb('#f0f4fb'), headFg: argb('#2b6cb0'),
         headAccDay: argb('#3182ce'), headAccNat: argb('#319795'), headBorder: argb('#dce4f0'),
         subHdrBg: argb('#eef2f8'), subHdrFg: argb('#4b5563'), subHdrBorder: argb('#d8e0ed'),
         partyBg: argb('#ffffff'), partyFg: argb('#111827'),
         partyAccent: argb('#38a169'), partyBorder: argb('#edf0f7'),
         invEven: argb('#ffffff'), invOdd: argb('#fafbfd'),
         invFg: argb('#111827'), invMuted: argb('#6b7280'),
         invBorder: argb('#eef1f7'), invRate: argb('#b7791f'), invTds: argb('#c53030'),
         salesBg: argb('#f0fff4'), salesFg: argb('#276749'),
         purchBg: argb('#ebf8ff'), purchFg: argb('#2b6cb0'),
         spacerBg: argb('#f5f7fa'),
      };

      const thin = (c) => ({ style: 'thin', color: { rgb: c } });
      const med = (c) => ({ style: 'medium', color: { rgb: c } });
      const none = () => ({ style: 'none' });
      const cl = (v, t, s = {}) => ({ v, t: t || 's', s });
      const fnt = (fg, bold = false, sz = 10) => ({ color: { rgb: fg }, bold, sz, name: 'Calibri' });
      const fl = (bg) => ({ type: 'pattern', patternType: 'solid', fgColor: { rgb: bg } });
      const bB = (c) => ({ top: none(), left: none(), right: none(), bottom: thin(c) });
      const bBm = (c) => ({ top: none(), left: none(), right: none(), bottom: med(c) });
      const bN = () => ({ top: none(), left: none(), right: none(), bottom: none() });

      // Summary uses 7 cols (no invoice sub-cols); detail uses 10
      const NC = isDetail ? 10 : 7;
      const col = (i) => String.fromCharCode(65 + i);

      const grouped = {};
      tdsData.forEach((company) => {
         const cName = company.companyName;
         if (!grouped[cName]) grouped[cName] = { meta: company, groups: {} };
         const items = isDaybookView ? company.tdsHeads : company.natures;
         items?.forEach((group) => {
            const gLabel = isDaybookView ? group.tdsHeadName : (group.natureOfPayment || 'NONE');
            if (!grouped[cName].groups[gLabel]) grouped[cName].groups[gLabel] = {
               parties: [], headAccent: isDaybookView ? C.headAccDay : C.headAccNat,
            };
            group.parties?.forEach(p => grouped[cName].groups[gLabel].parties.push(p));
         });
      });

      const wb = { SheetNames: [], Sheets: {} };

      Object.entries(grouped).forEach(([cName, { meta, groups }]) => {
         let coNet = 0, coTds = 0;
         Object.values(groups).forEach(({ parties }) =>
            parties.forEach(p => { coNet += p.totalAmount || 0; coTds += p.totalTdsAmount || 0; })
         );

         const ws = {};
         const merges = [];
         let R = 1;

         const mg = (r, c1, c2) => merges.push({ s: { r: r - 1, c: c1 }, e: { r: r - 1, c: c2 } });
         const row = (cells) => {
            for (let c = 0; c < NC; c++) {
               ws[col(c) + R] = cells[c] !== undefined ? cells[c]
                  : { v: '', t: 's', s: { fill: fl(C.spacerBg), border: bN(), font: fnt(C.invFg) } };
            }
            R++;
         };
         const spacer = () => {
            for (let c = 0; c < NC; c++)
               ws[col(c) + R] = { v: '', t: 's', s: { fill: fl(C.spacerBg), border: bN(), font: fnt(C.invFg, false, 4) } };
            R++;
         };

         // ── Title row ──────────────────────────────────────────────────────────
         mg(R, 0, NC - 1);
         row([cl(
            `TDS REPORT (${isDaybookView ? 'Daybook' : 'Nature of Payment'} | ${isDetail ? 'Detail' : 'Summary'}) — ${cName}`,
            's', {
            fill: fl(C.titleBg), font: fnt(C.titleFg, true, 13),
            alignment: { horizontal: 'left', vertical: 'center' }, border: bN(),
         }
         ), ...Array(NC - 1).fill({ v: '', t: 's', s: { fill: fl(C.titleBg), border: bN(), font: fnt(C.titleFg) } })]);

         // ── Meta row ───────────────────────────────────────────────────────────
         mg(R, 0, NC - 1);
         row([cl(
            `Period: ${dateRange.fromDate} → ${dateRange.toDate}   |   ${isDaybookView ? 'Daybook' : 'Nature of Payment'} View   |   ${isDetail ? 'Detail' : 'Summary'} Mode   |   Generated: ${new Date().toLocaleDateString('en-IN')}`,
            's', {
            fill: fl(C.metaBg), font: fnt(C.metaFg, false, 9),
            alignment: { horizontal: 'left', vertical: 'center' }, border: bN(),
         }
         ), ...Array(NC - 1).fill({ v: '', t: 's', s: { fill: fl(C.metaBg), border: bN(), font: fnt(C.metaFg) } })]);

         spacer();

         // ── Company band ───────────────────────────────────────────────────────
         const cIS = { fill: fl(C.compBg), font: fnt(C.compFg, true, 11), alignment: { horizontal: 'left', vertical: 'center' }, border: { ...bBm(C.compBorder), left: med(C.compAccent) } };
         const cRS = (fg) => ({ fill: fl(C.compBg), font: fnt(fg, true, 11), alignment: { horizontal: 'right', vertical: 'center' }, border: bBm(C.compBorder) });
         const cES = { fill: fl(C.compBg), font: fnt(C.compFg), border: bBm(C.compBorder) };

         if (isDetail) {
            mg(R, 0, 5);
            row([
               cl(`${cName}   |   GSTIN: ${meta.companyGstin}   |   PAN: ${meta.companyPan}`, 's', cIS),
               ...Array(5).fill({ v: '', t: 's', s: cIS }),
               cl(`NET AMOUNT   ₹${fmtN(coNet)}`, 's', cRS(C.compFg)),
               { v: '', t: 's', s: cES },
               cl(`TDS AMOUNT   ₹${fmtN(coTds)}`, 's', cRS(C.invTds)),
               { v: '', t: 's', s: cES },
            ]);
         } else {
            // Summary: 7 cols — label spans 3, then spacer, net, spacer, tds
            mg(R, 0, 2);
            row([
               cl(`${cName}   |   GSTIN: ${meta.companyGstin}   |   PAN: ${meta.companyPan}`, 's', cIS),
               ...Array(2).fill({ v: '', t: 's', s: cIS }),
               { v: '', t: 's', s: cES },
               cl(`NET AMOUNT   ₹${fmtN(coNet)}`, 's', cRS(C.compFg)),
               { v: '', t: 's', s: cES },
               cl(`TDS AMOUNT   ₹${fmtN(coTds)}`, 's', cRS(C.invTds)),
            ]);
         }
         spacer();

         // ── Column headers for summary mode ───────────────────────────────────
         if (!isDetail) {
            const shS = (align = 'left') => ({
               fill: fl(C.subHdrBg), font: fnt(C.subHdrFg, true, 9),
               alignment: { horizontal: align, vertical: 'center' },
               border: { top: thin(C.subHdrBorder), bottom: thin(C.subHdrBorder), left: none(), right: none() },
            });
            row([
               cl(isDaybookView ? 'TDS Head' : 'Nature of Payment', 's', shS()),
               cl('Party Name', 's', shS()),
               cl('PAN', 's', shS()),
               cl('', 's', shS()),
               cl('Net Amount', 's', shS('right')),
               cl('TDS Apply On', 's', shS('right')),
               cl('TDS Amount', 's', shS('right')),
            ]);
         }

         // ── Groups ─────────────────────────────────────────────────────────────
         Object.entries(groups).forEach(([gLabel, { parties, headAccent }]) => {
            let gNet = 0, gApply = 0, gTds = 0;
            parties.forEach(p => { gNet += p.totalAmount || 0; gApply += p.totalTdsApplyOnAmt || 0; gTds += p.totalTdsAmount || 0; });

            if (isDetail) {
               // ── Detail: head band ────────────────────────────────────────────
               const hS = (align = 'left', fg = C.headFg, bold = true) => ({
                  fill: fl(C.headBg), font: fnt(fg, bold, 10),
                  alignment: { horizontal: align, vertical: 'center' },
                  border: { ...bB(C.headBorder), left: med(headAccent) },
               });
               const hSR = (fg = C.headFg, bold = true) => ({
                  fill: fl(C.headBg), font: fnt(fg, bold, 10),
                  alignment: { horizontal: 'right', vertical: 'center' }, border: bB(C.headBorder),
               });
               mg(R, 0, 5);
               row([
                  cl(`${isDaybookView ? 'TDS Head' : 'Nature'}: ${gLabel}`, 's', hS('left', C.headFg, true)),
                  ...Array(5).fill({ v: '', t: 's', s: { fill: fl(C.headBg), border: { ...bB(C.headBorder), left: none() }, font: fnt(C.headFg) } }),
                  cl(`NET AMOUNT   ₹${fmtN(gNet)}`, 's', hSR(C.compFg, false)),
                  cl(`APPLY ON   ₹${fmtN(gApply)}`, 's', hSR(C.invMuted, false)),
                  cl(`TDS AMOUNT   ₹${fmtN(gTds)}`, 's', hSR(C.invTds, true)),
                  { v: '', t: 's', s: { fill: fl(C.headBg), border: bB(C.headBorder), font: fnt(C.headFg) } },
               ]);

               // ── Detail: party + invoices ─────────────────────────────────────
               parties.forEach((party) => {
                  const pS = (align = 'left', fg = C.partyFg, bold = false) => ({
                     fill: fl(C.partyBg), font: fnt(fg, bold, 10),
                     alignment: { horizontal: align, vertical: 'center' },
                     border: { ...bB(C.partyBorder), left: med(C.partyAccent) },
                  });
                  const pSR = (fg = C.partyFg, bold = false) => ({
                     fill: fl(C.partyBg), font: fnt(fg, bold, 10),
                     alignment: { horizontal: 'right', vertical: 'center' }, border: bB(C.partyBorder),
                  });
                  const pE = { v: '', t: 's', s: { fill: fl(C.partyBg), border: bB(C.partyBorder), font: fnt(C.partyFg) } };

                  mg(R, 0, 5);
                  row([
                     cl(party.partyPan ? `${party.partyName}   |   PAN: ${party.partyPan}` : party.partyName, 's', pS('left', C.partyFg, true)),
                     ...Array(5).fill({ v: '', t: 's', s: { fill: fl(C.partyBg), border: { ...bB(C.partyBorder), left: none() }, font: fnt(C.partyFg) } }),
                     cl(`NET AMOUNT   ₹${fmtN(party.totalAmount)}`, 's', pSR(C.invFg, true)),
                     cl(`APPLY ON   ₹${fmtN(party.totalTdsApplyOnAmt || 0)}`, 's', pSR(C.invMuted, false)),
                     cl(`TDS AMOUNT   ₹${fmtN(party.totalTdsAmount)}`, 's', pSR(C.invTds, true)),
                     pE,
                  ]);

                  // Invoice sub-header
                  const shS = (align = 'left') => ({
                     fill: fl(C.subHdrBg), font: fnt(C.subHdrFg, true, 8),
                     alignment: { horizontal: align, vertical: 'center' },
                     border: { top: thin(C.subHdrBorder), bottom: thin(C.subHdrBorder), left: none(), right: none() },
                  });
                  row([
                     cl('Date', 's', shS()), cl('Doc #', 's', shS()), cl('Daybook', 's', shS()),
                     cl('Party Name', 's', shS()), cl('PAN', 's', shS()),
                     cl('TDS Rate', 's', shS('center')),
                     cl('Net Amount', 's', shS('right')), cl('TDS Apply On', 's', shS('right')),
                     cl('TDS Amount', 's', shS('right')), cl('Type', 's', shS('center')),
                  ]);

                  // Invoice rows
                  (party.invoices || []).forEach((inv, idx) => {
                     const isEven = idx % 2 === 0;
                     const rowBg = isEven ? C.invEven : C.invOdd;
                     const isSales = (inv.type || '').toLowerCase() === 'sales';
                     const iS = (align = 'left', fg = C.invFg, bold = false) => ({
                        fill: fl(rowBg), font: fnt(fg, bold, 9),
                        alignment: { horizontal: align, vertical: 'center' }, border: bB(C.invBorder),
                     });
                     row([
                        cl(inv.transactionAt || '', 's', iS()),
                        cl(String(inv.docno || ''), 's', iS('left', C.invFg, true)),
                        cl(inv.daybookName || '', 's', iS()),
                        cl(party.partyName || '', 's', iS()),
                        cl(party.partyPan || '', 's', iS('left', C.invMuted)),
                        cl(`${inv.tdsRate}%`, 's', iS('center', C.invRate, true)),
                        cl(fmtN(num(inv.netAmount)), 's', iS('right')),
                        cl(num(inv.tdsApplyOnAmt) > 0 ? fmtN(num(inv.tdsApplyOnAmt)) : '-', 's', iS('right', C.invMuted)),
                        cl(fmtN(num(inv.tdsAmount)), 's', iS('right', C.invTds, true)),
                        cl(inv.type || '', 's', {
                           fill: fl(isSales ? C.salesBg : C.purchBg),
                           font: fnt(isSales ? C.salesFg : C.purchFg, true, 9),
                           alignment: { horizontal: 'center', vertical: 'center' }, border: bB(C.invBorder),
                        }),
                     ]);
                  });
               });
            } else {
               // ── Summary: flat rows — one row per party under each head ────────
               const hRowS = (align = 'left') => ({
                  fill: fl(C.headBg), font: fnt(C.headFg, true, 9),
                  alignment: { horizontal: align, vertical: 'center' },
                  border: { ...bB(C.headBorder), left: med(headAccent) },
               });
               // Head label spans col 0
               mg(R, 0, 0);

               parties.forEach((party, pIdx) => {
                  const isEven = pIdx % 2 === 0;
                  const rowBg = isEven ? C.partyBg : C.invOdd;
                  const pR = (align = 'left', fg = C.partyFg, bold = false) => ({
                     fill: fl(pIdx === 0 ? C.headBg : rowBg), font: fnt(pIdx === 0 ? C.headFg : fg, bold || pIdx === 0, 9),
                     alignment: { horizontal: align, vertical: 'center' },
                     border: bB(pIdx === 0 ? C.headBorder : C.partyBorder),
                  });
                  const leftBorder = pIdx === 0
                     ? { ...bB(C.headBorder), left: med(headAccent) }
                     : { ...bB(C.partyBorder), left: med(C.partyAccent) };

                  row([
                     // Col 0: head label only on first party row
                     { v: pIdx === 0 ? gLabel : '', t: 's', s: { fill: fl(pIdx === 0 ? C.headBg : rowBg), font: fnt(pIdx === 0 ? C.headFg : C.partyFg, pIdx === 0, 9), alignment: { horizontal: 'left', vertical: 'center' }, border: leftBorder } },
                     // Col 1: party name
                     cl(party.partyName || '', 's', pR('left', C.partyFg, true)),
                     // Col 2: PAN
                     cl(party.partyPan || '-', 's', pR('left', C.invMuted)),
                     // Col 3: spacer / rate info
                     cl(party.partyTdsRate ? `${party.partyTdsRate}%` : '-', 's', pR('center', C.invRate, true)),
                     // Col 4: net amount
                     cl(fmtN(party.totalAmount || 0), 's', pR('right', C.invFg, true)),
                     // Col 5: TDS apply on
                     cl(fmtN(party.totalTdsApplyOnAmt || 0), 's', pR('right', C.invMuted)),
                     // Col 6: TDS amount
                     cl(fmtN(party.totalTdsAmount || 0), 's', { fill: fl(pIdx === 0 ? C.headBg : rowBg), font: fnt(C.invTds, true, 9), alignment: { horizontal: 'right', vertical: 'center' }, border: bB(pIdx === 0 ? C.headBorder : C.partyBorder) }),
                  ]);
               });

            }

            if (isDetail) spacer();
         });

         ws['!ref'] = `A1:${col(NC - 1)}${R - 1}`;
         ws['!merges'] = merges;
         ws['!rows'] = [];

         if (isDetail) {
            ws['!cols'] = [{ wch: 14 }, { wch: 10 }, { wch: 28 }, { wch: 24 }, { wch: 14 }, { wch: 10 }, { wch: 22 }, { wch: 22 }, { wch: 22 }, { wch: 12 }];
         } else {
            ws['!cols'] = [{ wch: 28 }, { wch: 28 }, { wch: 14 }, { wch: 10 }, { wch: 22 }, { wch: 22 }, { wch: 22 }];
         }

         const safeName = cName.replace(/[\/*?:[\]]/g, '').substring(0, 31);
         wb.SheetNames.push(safeName);
         wb.Sheets[safeName] = ws;
      });

      const now = new Date();
      const modeTag = isDetail ? 'Detail' : 'Summary';
      XLSX.writeFile(wb, `TDS_Report_${reportType}_${modeTag}_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.xlsx`);
   } catch (err) {
      console.error('Excel export error:', err);
      alert('Failed to export Excel. Check console for details.');
   }
};

const exportToPdf = (tdsData, isDaybookView, dateRange, reportType, viewMode) => {
   if (!tdsData || tdsData.length === 0) { alert('No data to export'); return; }
   const isDetail = viewMode === 'detail';

   try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pW = doc.internal.pageSize.getWidth();
      const pH = doc.internal.pageSize.getHeight();
      const M = 8;
      const UW = pW - M * 2;

      const CLR = {
         compBg: [234, 240, 251], compAccent: [43, 108, 176], compText: [26, 32, 44], compBorder: [197, 211, 236],
         headBg: [240, 244, 251], headAccDay: [49, 130, 206], headAccNat: [49, 151, 149],
         headText: [43, 108, 176], headBorder: [220, 228, 240],
         subHdrBg: [238, 242, 248], subHdrText: [75, 85, 99], subHdrBdr: [216, 224, 237],
         partyBg: [255, 255, 255], partyAccent: [56, 161, 105], partyText: [17, 24, 39], partyBorder: [237, 240, 247],
         invEven: [255, 255, 255], invOdd: [250, 251, 253],
         invText: [17, 24, 39], invMuted: [107, 114, 128], invBorder: [238, 241, 247],
         invRate: [183, 121, 31], invTds: [197, 48, 48],
         salesBg: [240, 255, 244], salesText: [39, 103, 73],
         purchBg: [235, 248, 255], purchText: [43, 108, 176],
         netLabel: [43, 108, 176],
         tdsLabel: [197, 48, 48],
      };

      const viewLabel = isDaybookView ? 'Daybook' : 'Nature of Payment';
      const modeLabel = isDetail ? 'Detail' : 'Summary';

      const drawPageHeader = () => {
         doc.setFillColor(26, 32, 44);
         doc.rect(0, 0, pW, 8.5, 'F');
         doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(255, 255, 255);
         doc.text(`TDS REPORT — ${viewLabel} | ${modeLabel}`, M, 5.8);
         doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(160, 174, 192);
         doc.text(`Period: ${dateRange.fromDate} to ${dateRange.toDate}`, pW - M, 5.8, { align: 'right' });
      };

      const drawPageFooter = (pageNum, totalPages) => {
         doc.setFontSize(5.5); doc.setTextColor(...CLR.invMuted); doc.setFont('helvetica', 'normal');
         doc.text(
            `Page ${pageNum} of ${totalPages}   |   Generated: ${new Date().toLocaleDateString('en-IN')}   |   Confidential`,
            pW / 2, pH - 2, { align: 'center' }
         );
      };

      const drawBand = ({ y, h, fillRgb, accentRgb, accentW = 2.5, textLeft, netAmt, tdsAmt, textLeftRgb, fontSz = 6.5, indent = 3 }) => {
         doc.setFillColor(...fillRgb);
         doc.rect(M, y, UW, h, 'F');
         if (accentRgb) {
            doc.setFillColor(...accentRgb);
            doc.rect(M, y, accentW, h, 'F');
         }

         const midY = y + h * 0.65;
         const tx = M + (accentRgb ? indent + accentW : indent);

         doc.setFont('helvetica', 'bold');
         doc.setFontSize(fontSz);
         doc.setTextColor(...(textLeftRgb || CLR.compText));
         doc.text(textLeft, tx, midY, { maxWidth: UW - 115 });

         const tdsValX = pW - M - 2;
         const tdsVal = `${tdsAmt}`;
         doc.setFont('helvetica', 'bold');
         doc.setFontSize(fontSz);
         doc.setTextColor(...CLR.tdsLabel);
         doc.text(tdsVal, tdsValX, midY, { align: 'right' });

         const tdsValWidth = doc.getTextWidth(tdsVal);
         doc.setFont('helvetica', 'normal');
         doc.setFontSize(fontSz - 1.5);
         doc.setTextColor(...CLR.tdsLabel);
         doc.text('TDS AMOUNT', tdsValX - tdsValWidth - 2, midY, { align: 'right' });

         const tdsLabelWidth = doc.getTextWidth('TDS AMOUNT');
         const netBlockRightX = tdsValX - tdsValWidth - tdsLabelWidth - 8;
         const netVal = `${netAmt}`;
         doc.setFont('helvetica', 'bold');
         doc.setFontSize(fontSz);
         doc.setTextColor(...CLR.netLabel);
         doc.text(netVal, netBlockRightX, midY, { align: 'right' });

         const netValWidth = doc.getTextWidth(netVal);
         doc.setFont('helvetica', 'normal');
         doc.setFontSize(fontSz - 1.5);
         doc.setTextColor(...CLR.netLabel);
         doc.text('NET AMOUNT', netBlockRightX - netValWidth - 2, midY, { align: 'right' });

         return y + h;
      };

      let isFirstPage = true;

      tdsData.forEach((company) => {
         if (!isFirstPage) doc.addPage();
         isFirstPage = false;
         drawPageHeader();
         let y = 10.5;

         const items = isDaybookView ? company.tdsHeads : company.natures;
         let coNet = 0, coTds = 0;
         items?.forEach(g => g.parties?.forEach(p => { coNet += p.totalAmount || 0; coTds += p.totalTdsAmount || 0; }));

         // Company band
         y = drawBand({
            y, h: 6.5, fillRgb: CLR.compBg, accentRgb: CLR.compAccent, accentW: 3,
            textLeft: `${company.companyName}   |   GSTIN: ${company.companyGstin}   |   PAN: ${company.companyPan}`,
            netAmt: fmtExport(coNet), tdsAmt: fmtExport(coTds),
            textLeftRgb: CLR.compText, fontSz: 7, indent: 2,
         });
         y += 1;

         if (isDetail) {
            // ── Detail mode: head → party band → invoice table ─────────────────
            items?.forEach((group) => {
               const gLabel = isDaybookView ? group.tdsHeadName : (group.natureOfPayment || 'NONE');
               const headAccent = isDaybookView ? CLR.headAccDay : CLR.headAccNat;
               let gNet = 0, gTds = 0;
               group.parties?.forEach(p => { gNet += p.totalAmount || 0; gTds += p.totalTdsAmount || 0; });

               if (y > 192) { doc.addPage(); drawPageHeader(); y = 10.5; }

               y = drawBand({
                  y, h: 5.5, fillRgb: CLR.headBg, accentRgb: headAccent, accentW: 2,
                  textLeft: (isDaybookView ? 'TDS Head: ' : 'Nature: ') + gLabel,
                  netAmt: fmtExport(gNet), tdsAmt: fmtExport(gTds),
                  textLeftRgb: CLR.headText, fontSz: 6.5, indent: 2,
               });
               y += 0.5;

               group.parties?.forEach((party) => {
                  if (y > 192) { doc.addPage(); drawPageHeader(); y = 10.5; }

                  y = drawBand({
                     y, h: 5.5, fillRgb: CLR.partyBg, accentRgb: CLR.partyAccent, accentW: 2,
                     textLeft: `${party.partyName}${party.partyPan ? `   |   PAN: ${party.partyPan}` : ''}   |   Rate: ${party.partyTdsRate}%`,
                     netAmt: fmtExport(party.totalAmount), tdsAmt: fmtExport(party.totalTdsAmount),
                     textLeftRgb: CLR.partyText, fontSz: 6.5, indent: 2,
                  });
                  y += 0.4;

                  const tableRows = (party.invoices || []).map(inv => [
                     inv.transactionAt || '',
                     String(inv.docno || ''),
                     inv.daybookName || '',
                     `${inv.tdsRate}%`,
                     fmtExport(inv.netAmount),
                     Number(inv.tdsApplyOnAmt) > 0 ? fmtExport(inv.tdsApplyOnAmt) : '-',
                     fmtExport(inv.tdsAmount),
                     (inv.type || '').charAt(0).toUpperCase() + (inv.type || '').slice(1),
                  ]);

                  if (tableRows.length === 0) { y += 0.5; return; }
                  if (y > 192) { doc.addPage(); drawPageHeader(); y = 10.5; }

                  doc.autoTable({
                     startY: y,
                     head: [['Date', 'Doc #', 'Daybook / Voucher', 'TDS %', 'Net Amount', 'TDS Apply On', 'TDS Amount', 'Type']],
                     body: tableRows,
                     theme: 'plain', tableWidth: UW, margin: { left: M, right: M },
                     styles: {
                        fontSize: 6.5, cellPadding: { top: 1.2, right: 2.5, bottom: 1.2, left: 2.5 },
                        textColor: CLR.invText, lineColor: CLR.invBorder, lineWidth: 0.1,
                        valign: 'middle', overflow: 'ellipsize', minCellHeight: 5.5,
                     },
                     headStyles: {
                        fillColor: CLR.subHdrBg, textColor: CLR.subHdrText, fontStyle: 'bold',
                        fontSize: 6, halign: 'center',
                        cellPadding: { top: 1.2, right: 2.5, bottom: 1.2, left: 2.5 },
                        minCellHeight: 5, lineColor: CLR.subHdrBdr, lineWidth: 0.2,
                     },
                     columnStyles: {
                        0: { halign: 'left', cellWidth: 24 },
                        1: { halign: 'center', cellWidth: 14 },
                        2: { halign: 'left', cellWidth: 'auto' },
                        3: { halign: 'center', cellWidth: 13, textColor: CLR.invRate, fontStyle: 'bold' },
                        4: { halign: 'right', cellWidth: 30 },
                        5: { halign: 'right', cellWidth: 28, textColor: CLR.invMuted },
                        6: { halign: 'right', cellWidth: 30, textColor: CLR.invTds, fontStyle: 'bold' },
                        7: { halign: 'center', cellWidth: 20 },
                     },
                     didParseCell: (data) => {
                        if (data.section === 'body') {
                           data.cell.styles.fillColor = data.row.index % 2 === 0 ? CLR.invEven : CLR.invOdd;
                           if (data.column.index === 7) {
                              const v = (data.cell.raw || '').toLowerCase();
                              data.cell.styles.fillColor = v === 'sales' ? CLR.salesBg : CLR.purchBg;
                              data.cell.styles.textColor = v === 'sales' ? CLR.salesText : CLR.purchText;
                              data.cell.styles.fontStyle = 'bold';
                           }
                        }
                     },
                     didDrawPage: () => { drawPageHeader(); },
                  });

                  y = doc.lastAutoTable.finalY + 1.5;
               });
               y += 1;
            });
         } else {
            // ── Summary mode: one table per company with head/party rows ─────────
            items?.forEach((group) => {
               const gLabel = isDaybookView ? group.tdsHeadName : (group.natureOfPayment || 'NONE');
               const headAccent = isDaybookView ? CLR.headAccDay : CLR.headAccNat;
               let gNet = 0, gApply = 0, gTds = 0;
               group.parties?.forEach(p => { gNet += p.totalAmount || 0; gApply += p.totalTdsApplyOnAmt || 0; gTds += p.totalTdsAmount || 0; });

               if (y > 192) { doc.addPage(); drawPageHeader(); y = 10.5; }

               // Head band (thinner)
               y = drawBand({
                  y, h: 5, fillRgb: CLR.headBg, accentRgb: headAccent, accentW: 2,
                  textLeft: (isDaybookView ? 'TDS Head: ' : 'Nature: ') + gLabel,
                  netAmt: fmtExport(gNet), tdsAmt: fmtExport(gTds),
                  textLeftRgb: CLR.headText, fontSz: 6.5, indent: 2,
               });
               y += 0.3;

               // Party summary table
               const sumRows = (group.parties || []).map(p => [
                  p.partyName || '',
                  p.partyPan || '-',
                  p.partyTdsRate ? `${p.partyTdsRate}%` : '-',
                  fmtExport(p.totalAmount || 0),
                  fmtExport(p.totalTdsApplyOnAmt || 0),
                  fmtExport(p.totalTdsAmount || 0),
               ]);

               if (y > 192) { doc.addPage(); drawPageHeader(); y = 10.5; }

               doc.autoTable({
                  startY: y,
                  head: [['Party Name', 'PAN', 'TDS Rate', 'Net Amount', 'TDS Apply On', 'TDS Amount']],
                  body: sumRows,
                  theme: 'plain', tableWidth: UW, margin: { left: M, right: M },
                  styles: {
                     fontSize: 6.5, cellPadding: { top: 1.2, right: 3, bottom: 1.2, left: 3 },
                     textColor: CLR.invText, lineColor: CLR.invBorder, lineWidth: 0.1,
                     valign: 'middle', overflow: 'ellipsize', minCellHeight: 5,
                  },
                  headStyles: {
                     fillColor: CLR.subHdrBg, textColor: CLR.subHdrText, fontStyle: 'bold',
                     fontSize: 6, halign: 'center',
                     cellPadding: { top: 1.2, right: 3, bottom: 1.2, left: 3 },
                     minCellHeight: 4.5, lineColor: CLR.subHdrBdr, lineWidth: 0.2,
                  },
                  columnStyles: {
                     0: { halign: 'left', cellWidth: 'auto' },
                     1: { halign: 'center', cellWidth: 30 },
                     2: { halign: 'center', cellWidth: 18, textColor: CLR.invRate, fontStyle: 'bold' },
                     3: { halign: 'right', cellWidth: 38 },
                     4: { halign: 'right', cellWidth: 38, textColor: CLR.invMuted },
                     5: { halign: 'right', cellWidth: 38, textColor: CLR.invTds, fontStyle: 'bold' },
                  },
                  didParseCell: (data) => {
                     if (data.section === 'body') {
                        data.cell.styles.fillColor = data.row.index % 2 === 0 ? CLR.invEven : CLR.invOdd;
                     }
                  },
                  didDrawPage: () => { drawPageHeader(); },
               });

               y = doc.lastAutoTable.finalY + 2;
            });
         }
      });

      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) { doc.setPage(i); drawPageFooter(i, totalPages); }

      const now = new Date();
      const modeTag = isDetail ? 'Detail' : 'Summary';
      doc.save(`TDS_Report_${reportType}_${modeTag}_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.pdf`);
   } catch (err) {
      console.error('PDF export error:', err);
      alert('Failed to export PDF. Check console for details.');
   }
};

const TDSREPORT = () => {
   const textColor = useColorModeValue('gray.800', 'white');
   const backgroundColor = useColorModeValue('white', '#101F4A');
   const bg = useColorModeValue('#f2f7f2', '#1a254b');
   const icon = useColorModeValue('#000000', '#ffffff');
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const { user } = useUser();
   const { companyList, company } = useGlobal();
   const dispatch = useDispatch();
   const tdsData = useSelector((state) => state.reports?.tds || []);
   const dbName = company?.dbName;

   const abortRef = useRef(null);
   const wrapperRef = useRef();
   const searchTimeout = useRef();

   const [companyFilter, setCompanyFilter] = useState([]);
   const [expandedItems, setExpandedItems] = useState({});
   const [searchQuery, setSearchQuery] = useState('');
   const [isLoading, setIsLoading] = useState(false);
   const [isFilterLoad, setIsFilterLoad] = useState(false);
   const [reportType, setReportType] = useState('daybook');
   const [viewMode, setViewMode] = useState('detail');
   const [expanded, setExpanded] = useState(false);
   const [dateRange, setDateRange] = useState(() => {
      const today = new Date();
      const from = new Date(); from.setDate(today.getDate() - 6);
      return { fromDate: from.toISOString().split('T')[0], toDate: today.toISOString().split('T')[0] };
   });

   const { isOpen, onOpen, onClose } = useDisclosure();
   useOutsideClick({ ref: wrapperRef, handler: () => { if (!searchQuery) setExpanded(false); } });

   const companyOptions = useMemo(() => getShareCompanyOptions({ dbName, company: companyList }), [companyList, dbName]);
   const lastcompanyOptions = useMemo(() => {
      const currentUserCode = user?.loginhistory?.code;
      const currentCompanyId = user?.loginhistory?.companyId;

      // SHARED COMPANY MODE: When code is present
      if (currentUserCode) {
         // Find the company with matching ID AND matching dbName
         const sharedCompany = companyList.find(
            company => company.id === currentCompanyId && company.dbName === currentUserCode
         );

         if (sharedCompany) {
            return [{
               value: sharedCompany.id,
               label: sharedCompany.name,
               id: sharedCompany.id,
               name: sharedCompany.name,
            }];
         }

         // Fallback: try to find by dbName only
         const companiesWithDbName = companyList.filter(company => company.dbName === currentUserCode);
         if (companiesWithDbName.length > 0) {
            return companiesWithDbName.map(company => ({
               value: company.id,
               label: company.name,
               id: company.id,
               name: company.name,
            }));
         }

         return [];
      }

      // NORMAL MODE: When no code, show companies without dbName
      return companyList
         .filter(company => !company.dbName)
         .map(company => ({
            value: company.id,
            label: company.name,
            id: company.id,
            name: company.name,
         }));
   }, [companyList, user?.loginhistory?.code, user?.loginhistory?.companyId]);

   const companyIds = useMemo(() =>
      companyFilter.length > 0
         ? companyFilter
         : localStorage.getItem('companyId') ? [parseInt(localStorage.getItem('companyId'))] : [],
      [companyFilter]);

   const storedCompanyId = localStorage.getItem('companyId');
   useEffect(() => {
      if (storedCompanyId && companyList.length > 0) {
         const id = parseInt(storedCompanyId);
         const match = companyList.find(c => c.id === id);
         if (match) setCompanyFilter([match.id]);
      }
   }, [companyList, storedCompanyId]);

   useEffect(() => {
      if (!tdsData?.length) return;
      const next = {};
      tdsData.forEach(co => {
         next[`c-${co.companyId}`] = true;
         next[`nc-${co.companyId}`] = true;
         co.tdsHeads?.forEach(h => { next[`h-${co.companyId}-${h.tdsAccountId}`] = true; });
         co.natures?.forEach((n, i) => { next[`nn-${co.companyId}-${i}`] = true; });
         if (viewMode === 'detail') {
            co.tdsHeads?.forEach(h => {
               h.parties?.forEach(p => { next[`p-${co.companyId}-${h.tdsAccountId}-${p.partyAccountId}`] = true; });
            });
            co.natures?.forEach((n, i) => {
               n.parties?.forEach(p => { next[`np-${co.companyId}-${i}-${p.partyAccountId}`] = true; });
            });
         }
      });
      setExpandedItems(next);
   }, [tdsData, viewMode]);

   const fetchData = useCallback(async (term = '') => {
      if (!Array.isArray(companyIds) || !companyIds.length) return;
      if (!dateRange.fromDate || !dateRange.toDate) return;
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      (term ? setIsFilterLoad : setIsLoading)(true);
      try {
         const ids = companyIds.filter(id => !isNaN(+id) && +id > 0);
         if (ids.length) {
            await dispatch(getReportTds({
               companyIds: ids.join(','), search: term.trim(),
               fromDate: dateRange.fromDate, toDate: dateRange.toDate, type: reportType,
            }));
         }
      } catch (e) { if (e.name !== 'AbortError') console.error(e); }
      finally { setIsLoading(false); setIsFilterLoad(false); abortRef.current = null; }
   }, [dispatch, companyIds, dateRange.fromDate, dateRange.toDate, reportType]);

   useEffect(() => {
      const ok = Array.isArray(companyIds) && companyIds.length > 0 && !companyIds.some(id => isNaN(+id) || +id <= 0);
      if (ok && dateRange.fromDate && dateRange.toDate) fetchData();
   }, [companyIds, dateRange.fromDate, dateRange.toDate, reportType]);

   useEffect(() => () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
      if (abortRef.current) abortRef.current.abort();
   }, []);

   const handleSearch = useCallback((e) => {
      const v = e.target.value; setSearchQuery(v);
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
      searchTimeout.current = setTimeout(() => fetchData(v), DEBOUNCE_DELAY);
   }, [fetchData]);

   const handleDates = useCallback((dates) => {
      if (!dates || dates.length !== 2) return;
      const [from, to] = dates.map(d => typeof d === 'string' ? d : d.toISOString().slice(0, 10));
      setDateRange({ fromDate: from, toDate: to });
   }, []);

   const handleCompany = useCallback(sel => setCompanyFilter(sel || []), []);
   const handleRefresh = useCallback(() => fetchData(searchQuery), [fetchData, searchQuery]);
   const toggleExpand = useCallback(id => setExpandedItems(p => ({ ...p, [id]: !p[id] })), []);
   const expandAll = useCallback(() => setExpandedItems(p => Object.fromEntries(Object.keys(p).map(k => [k, true]))), []);
   const collapseAll = useCallback(() => setExpandedItems(p => Object.fromEntries(Object.keys(p).map(k => [k, false]))), []);

   const reportTypeOptions = [
      { value: 'daybook', label: 'Daybook' },
      { value: 'natureOfPayment', label: 'Nature of Payment' },
   ];
   const viewModeOptions = [
      { value: 'detail', label: 'Detail' },
      { value: 'summary', label: 'Summary' },
   ];

   const hasData = tdsData?.length > 0;
   const isDaybookView = reportType === 'daybook';
   const dataHasHeads = hasData && tdsData[0]?.tdsHeads;
   const dataHasNatures = hasData && tdsData[0]?.natures;

   const ss = {
      control: (b, s) => ({
         ...b, minHeight: '33px', height: '33px',
         backgroundColor: isDark ? '#2D3748' : '#fff',
         borderColor: s.isFocused ? (isDark ? '#63B3ED' : '#3182CE') : (isDark ? '#4A5568' : '#E2E8F0'),
         boxShadow: s.isFocused ? (isDark ? '0 0 0 1px #63B3ED' : '0 0 0 1px #3182CE') : 'none',
      }),
      valueContainer: b => ({ ...b, height: '33px', padding: '0 8px' }),
      indicatorsContainer: b => ({ ...b, height: '33px' }),
      singleValue: b => ({ ...b, color: isDark ? 'white' : '#1a202c', fontSize: '13px' }),
      placeholder: b => ({ ...b, color: isDark ? '#718096' : '#A0AEC0', fontSize: '13px' }),
      option: (b, s) => ({
         ...b, fontSize: '13px', color: isDark ? 'white' : '#1a202c',
         backgroundColor: s.isFocused ? (isDark ? '#4A5568' : '#EDF2F7') : (isDark ? '#2D3748' : '#fff'),
      }),
      menu: b => ({ ...b, backgroundColor: isDark ? '#2D3748' : '#fff', zIndex: 10 }),
   };

   return (
      <>
         <Flex pr="25px" mb="5px" justifyContent="space-between" align="center" mt="50px">
            <Flex align="center">
               <img src={back} alt="Go Back" width="15px" onClick={() => window.history.back()} style={{ cursor: 'pointer' }} />
               <Text color={textColor} fontSize="15px" fontWeight="700" ml={5}>TDS Report</Text>
            </Flex>
            <Flex gap={2}>
               <Button size="sm" variant="outline" onClick={expandAll} leftIcon={<MdExpand />} fontSize="12px">Expand All</Button>
               <Button size="sm" variant="outline" onClick={collapseAll} leftIcon={<MdCompress />} fontSize="12px">Collapse All</Button>
            </Flex>
         </Flex>

         <Box mt="15px" bg={backgroundColor} px={4} py={3} borderRadius="7px">
            <Flex flexWrap="wrap" gap={3} alignItems="center" justifyContent="space-between">
               <Flex flex="1" gap={3} flexWrap="wrap" alignItems="flex-end">
                  <Box width="300px" fontSize="13px" fontWeight={500}>
                     <GridItem>
                        {renderDynamicDropdown('company', 'Company', lastcompanyOptions,
                           { value: companyFilter, onChange: handleCompany },
                           {}, textColor, false, 'Select company', true, undefined, true)}
                     </GridItem>
                  </Box>
                  <Box>
                     <PeriodDatePicker
                        value={[new Date(dateRange.fromDate), new Date(dateRange.toDate)]}
                        onChange={handleDates} defaultPeriod="last7Days"
                        style={{ width: 240, borderRadius: 5, height: 33 }} />
                  </Box>
                  <Box width="170px">
                     <Text fontSize="12px" fontWeight={500} mb={1} color={isDark ? 'whiteAlpha.700' : 'gray.600'}>Report Type</Text>
                     <Select options={reportTypeOptions} value={reportTypeOptions.find(o => o.value === reportType)}
                        onChange={sel => setReportType(sel.value)} styles={ss} />
                  </Box>
                  <Box width="140px">
                     <Text fontSize="12px" fontWeight={500} mb={1} color={isDark ? 'whiteAlpha.700' : 'gray.600'}>View</Text>
                     <Select options={viewModeOptions} value={viewModeOptions.find(o => o.value === viewMode)}
                        onChange={sel => setViewMode(sel.value)} styles={ss} />
                  </Box>
               </Flex>

               <Flex align="center" gap={2}>
                  <MotionFlex ref={wrapperRef} align="center" bg={bg} borderRadius="full"
                     cursor="pointer" px={expanded ? 3 : 0} w={expanded ? '210px' : '36px'} h="36px"
                     transition="all 0.3s ease" onClick={() => setExpanded(true)}>
                     <MotionSearchIcon color={icon} animate={{ rotate: expanded ? 0 : 90 }}
                        transition={{ duration: 0.3 }}
                        style={{ marginRight: expanded ? '8px' : '0', marginLeft: expanded ? '0' : '8px' }} />
                     {expanded && (
                        <InputGroup width="170px">
                           <Input placeholder="Search..." value={searchQuery} onChange={handleSearch}
                              color={icon} variant="unstyled" fontSize="13px" _focus={{ outline: 'none' }} />
                           {isFilterLoad && <Spinner size="sm" position="absolute" right="6px" top="50%" transform="translateY(-50%)" />}
                        </InputGroup>
                     )}
                  </MotionFlex>

                  <Button onClick={handleRefresh} isLoading={isLoading} variant="ghost" size="sm" p={2}>
                     <LuRefreshCcw size={18} />
                  </Button>

                  <Popover>
                     <PopoverTrigger>
                        <img src={Download} alt="Download" width="28" style={{ cursor: 'pointer' }} />
                     </PopoverTrigger>
                     <PopoverContent width="190px" bg={backgroundColor} mr={8}>
                        <PopoverBody p={3}>
                           {/* ✅ Pass viewMode to both export functions */}
                           <Button width="100%" variant="ghost" justifyContent="space-between" mb={2} size="sm"
                              _hover={{ bg: isDark ? 'whiteAlpha.100' : 'gray.100' }}
                              onClick={() => exportToExcel(tdsData, isDaybookView, dateRange, reportType, viewMode)}>
                              <img src={xls} alt="XLS" width="22px" /> Download Excel
                           </Button>
                           <Button width="100%" variant="ghost" justifyContent="space-between" size="sm"
                              _hover={{ bg: isDark ? 'whiteAlpha.100' : 'gray.100' }}
                              onClick={() => exportToPdf(tdsData, isDaybookView, dateRange, reportType, viewMode)}>
                              <img src={xls} alt="PDF" width="22px" /> Download PDF
                           </Button>
                        </PopoverBody>
                     </PopoverContent>
                  </Popover>

                  <Flex display={{ base: 'flex', xl: 'none' }}>
                     <Button variant="unstyled" onClick={onOpen}>
                        <Image src={DotIcon} alt="Menu" width="25px" />
                     </Button>
                  </Flex>
               </Flex>
            </Flex>
         </Box>

         <Box bg={backgroundColor} mt="15px" borderRadius="7px" overflow="hidden" minH="400px">
            {isLoading ? (
               <Box p={6}><Loader /></Box>
            ) : !hasData ? (
               <Box textAlign="center" py={10}>
                  <img src={nodata} alt="No Data" width="200px" />
                  <Text mt={4} color={isDark ? 'whiteAlpha.600' : 'gray.500'} fontSize="14px">
                     No TDS data found for selected filters
                  </Text>
               </Box>
            ) : (
               <Box overflowX="auto" overflowY="auto" maxH="calc(100vh - 270px)">
                  <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                     <tbody>
                        {isDaybookView && dataHasHeads && (
                           <DaybookRows data={tdsData} isDark={isDark}
                              expandedItems={expandedItems} toggleExpand={toggleExpand} viewMode={viewMode} />
                        )}
                        {!isDaybookView && dataHasNatures && (
                           <NatureRows data={tdsData} isDark={isDark}
                              expandedItems={expandedItems} toggleExpand={toggleExpand} viewMode={viewMode} />
                        )}
                        {((isDaybookView && !dataHasHeads) || (!isDaybookView && !dataHasNatures)) && (
                           <tr>
                              <td colSpan={TOTAL_COLS} style={{
                                 textAlign: 'center', padding: '40px',
                                 color: isDark ? '#f6ad55' : '#c05621', fontSize: '13px',
                              }}>
                                 Data does not match selected report type. Try refreshing.
                              </td>
                           </tr>
                        )}
                     </tbody>
                  </table>
               </Box>
            )}
         </Box>

         <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
            <DrawerOverlay />
            <DrawerContent borderTopRadius="20px">
               <DrawerCloseButton />
               <DrawerHeader borderBottomWidth="1px" fontSize="14px">Filters</DrawerHeader>
               <DrawerBody pb={6}>
                  <GridItem>
                     {renderDynamicDropdown('company', 'Company', lastcompanyOptions,
                        { value: companyFilter, onChange: handleCompany },
                        {}, textColor, false, 'Select company', true, undefined, true)}
                  </GridItem>
                  <Box mt={3}>
                     <PeriodDatePicker
                        value={[new Date(dateRange.fromDate), new Date(dateRange.toDate)]}
                        onChange={handleDates} defaultPeriod="last7Days"
                        style={{ width: '100%', borderRadius: 5, height: 35 }} />
                  </Box>
                  <Box mt={3}>
                     <Text mb={1} fontSize="12px" fontWeight={500} color={isDark ? 'whiteAlpha.700' : 'gray.600'}>Report Type</Text>
                     <Select options={reportTypeOptions} value={reportTypeOptions.find(o => o.value === reportType)}
                        onChange={sel => setReportType(sel.value)} styles={ss} />
                  </Box>
                  <Box mt={3}>
                     <Text mb={1} fontSize="12px" fontWeight={500} color={isDark ? 'whiteAlpha.700' : 'gray.600'}>View</Text>
                     <Select options={viewModeOptions} value={viewModeOptions.find(o => o.value === viewMode)}
                        onChange={sel => setViewMode(sel.value)} styles={ss} />
                  </Box>
                  <Button colorScheme="blue" width="100%" onClick={onClose} mt={4} size="sm">Apply</Button>
               </DrawerBody>
            </DrawerContent>
         </Drawer>
      </>
   );
};

export default TDSREPORT;